import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take, finalize } from 'rxjs/operators';
import { AuthService } from '@services/auth/auth.service';
import { Router } from '@angular/router';
import { Util } from '@/utils/util';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private authService: AuthService,
    private router: Router,
    private util: Util
  ) {}

intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
  const isExcludedRequest =
    req.url.includes('/auth/login') ||
    req.url.includes('/auth/refresh-token') ||
    req.url.includes('/auth/2fa/activate') ||
    req.url.includes('/auth/2fa/setup') ||
    req.url.includes('/auth/2fa/validate');


  if (isExcludedRequest) {
    return next.handle(req);
  }

  const token = this.authService.getAuthToken();

  // 🔒 Si el token está ausente o inválido, forzar logout
  if (!token || this.authService.isTokenExpired(token)) {
    this.authService.logout();
    return throwError(() => new Error('Token ausente o expirado'));
  }

  // 🔁 Token válido, agregar encabezado y continuar
  const authReq = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });

  return next.handle(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 || error.status === 403) {
        this.authService.logout();
      }
      return throwError(() => error);
    })
  );
}


  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const refreshToken = this.authService.getRefreshToken();
    if (!refreshToken) {
      this.handleSessionExpired();
      return throwError(() => new Error('Sesión expirada'));
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap((res: any) => {
          if (res?.token) {
            this.authService.setToken(res.token);
            this.authService.setRefreshToken(res.refreshToken);
            this.authService['scheduleTokenRefresh'](res.token); // ← sincroniza reloj

            this.refreshTokenSubject.next(res.token);

            const retryReq = request.clone({
              setHeaders: { Authorization: `Bearer ${res.token}` }
            });
            return next.handle(retryReq);
          } else {
            this.handleSessionExpired();
            return throwError(() => new Error('Refresh fallido'));
          }
        }),
        catchError(err => {
          this.handleSessionExpired();
          return throwError(() => err);
        }),
        finalize(() => {
          this.isRefreshing = false;
        })
      );
    } else {
      // Si ya hay un refresh en curso, espera que termine
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          const retryReq = request.clone({
            setHeaders: { Authorization: `Bearer ${token}` }
          });
          return next.handle(retryReq);
        })
      );
    }
  }

  private handleSessionExpired(): void {
    this.authService.logout();
    this.util.errorMessage('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
  }
}
